<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        
        <div class="">

            <h4>Invite Suppliers</h4>
            
            <div class="text-muted mb-4">
                <p>
                    Use the tools below to invite suppliers to the RFx.
                </p>
                <p>
                    You can also adjust the settings to publish the RFx in your Supplier Portal and invite suppliers with a special sharing link (if you have enabled open portal signups).
                </p>
            </div>

        </div>

        <b-row>
            <b-col cols="12" md="7">

                <b-card
                    class="shadow-sm"
                >

                    <div>

                        <div class="d-flex gap-2 flex-wrap justify-content-between align-items-center mb-3">
                            <h4 class="mb-0">
                                Invited Suppliers
                                <b-badge variant="soft-primary" class="ml-2">{{responseList.length}}</b-badge>
                            </h4>
                            <div class="flex-fill d-flex flex-wrap align-items-center justify-content-end gap-3">
                                <b-button v-if="rfxData && rfxData.responses_to_remind_count" variant="gray-primary" class="" @click="startBulkReminder">
                                    Send Reminders
                                </b-button>
                                <rfx-vendor-invite-modal
                                    ref="vendorInviteModal"
                                    :rfx-pk="rfxPk"
                                    :invite-email-text="invite_email_text"
                                    :invited-vendor-ids="invitedVendorIdList"
                                    :can-send-invites="rfxData.can_send_invites"
                                    @new-invite="getInvitesList"
                                ></rfx-vendor-invite-modal>
                                <b-button v-if="rfxData && !loadingList" variant="success" class="" @click="bulkInviteModalShowing = true">
                                    <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                    Bulk Invite
                                </b-button>
                            </div>
                        </div>

                        <base-single-collapse
                            button-text="Instructions"
                            class="mb-2"
                        >
                            <ul class="list-flush mb-2 small">
                                <li>Invite individual suppliers from your saved Vendor Database or with the email address of a new supplier.</li>
                                <li>Vendors will receive an invitation email to this sourcing event with instructions for submitting their response.</li>
                                <li>A new login account on your Supplier Portal will automatically be created for each supplier recipient if they don't already have one.</li>
                                <li>If the Sourcing Event is not yet Open, invitations will be queued and sent once the event is opened.</li>
                                <li>To change the invited contacts for a supplier already invited, go to the Manage Responses page.</li>
                            </ul>
                        </base-single-collapse>
                        
                        <b-table 
                            :items="responseList" 
                            primary-key="id"
                            :fields="responseListFields" 
                            :busy="loadingList"
                            class="no-top-border"
                            tbody-class="td-align-middle"
                            small
                            show-empty
                        >
                            <!-- <template #table-busy>
                                <busy-spinner inner-class="py-5"></busy-spinner>
                            </template> -->
                            <template #empty>
                                <busy-spinner v-if="loadingList" inner-class="py-5"></busy-spinner>
                                <div v-else class="d-flex justify-content-center align-items-center py-4">
                                    No Invitations Yet
                                </div>
                            </template>
                            <template #cell(vendor_name)="data">
                                <div class="my-2">
                                    <div class="font-weight-bold font-size-90">{{data.value}}</div>
                                    <span :id="'invited-contacts-' + data.item.id" class="underline-dashed text-muted">
                                        <b-icon icon="people" class="mr-2"></b-icon>
                                        <span>{{ data.item.invited_tus.length.toString()}} contacts</span>
                                    </span>
                                </div>
                                <b-popover :target="'invited-contacts-' + data.item.id" triggers="hover" placement="right">
                                    <template #title>Invited Contacts</template>
                                    <ul class="list-flush mb-0">
                                        <li v-for="tu in data.item.invited_tus" :key="tu.id">{{tu.full_name}}</li>
                                    </ul>
                                </b-popover>
                            </template>
                            <template #cell(sm_state)="data">
                                <div v-if="data.value" key="has_response_status">
                                    <status-object-badge :status-object="data.item.state_dict" text-uppercase font-size="0.7rem"></status-object-badge>
                                </div>
                                <span v-else key="blank_response_status" class="chip text-uppercase font-size-70 chip-light2">
                                    Not Started
                                </span>
                            </template>

                            <template #cell(invitation_date)="data">
                                <span v-if="data.value">{{ data.value | dateFilter }}</span>
                                <span v-else>Not Invited</span>
                            </template>

                            <template #cell(actions)="data">
                                <b-button 
                                    v-if="data.item.is_submitted && data.item.manage_response_url" 
                                    key="submittedActions"
                                    size="sm" 
                                    variant="minimal-primary"
                                    class="no-shadow"
                                    :href="data.item.manage_response_url"
                                >
                                    View Response
                                    <b-icon icon="box-arrow-up-right" class="ml-1"></b-icon>
                                </b-button>
                                <!-- MAYBE SHOULD FETCH THESE ON DEMAND - VERY EXPENSIVE -->
                                <b-dropdown 
                                    v-else-if="data.item.possible_transitions && data.item.possible_transitions.length" 
                                    key="notSubmittedActions"
                                    size="sm" 
                                    variant="action-round" 
                                    menu-class="shadow"
                                    toggle-class=""
                                    no-caret
                                >
                                    <template #button-content>
                                        <b-icon icon="three-dots"></b-icon>
                                    </template>
                                    <!-- PUT POSSIBLE TRANSITIONS HERE -->
                                    <b-dropdown-item 
                                        v-for="transObj in data.item.possible_transitions"
                                        :key="transObj.trigger"
                                        @click="triggerTransition(transObj, 'rfxResponse.call_transition', data.item.id)"
                                    >
                                        <b-icon :icon="transObj.icon" :variant="transObj.color_variant" class="mr-2"></b-icon>
                                        {{ transObj.label }}
                                    </b-dropdown-item>
                                    <!-- Add any non state-machine transitions here -->
                                </b-dropdown>
                            </template>

                        </b-table>

                    </div>
                </b-card>

            </b-col>

            <b-col cols="12" md="5">

                <div class="w-100 border rounded shadow-sm bg-white p-3">
                    
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <h4>Invitation Email</h4>
                            <p class="text-muted small mb-2">
                                Optional boilerplate text included in the vendor invitation emails. 
                            </p>
                            <p class="text-muted small mb-2">
                                You have the chance to modify it for each vendor just before you send each invitation.
                            </p>
                            <p class="text-muted small mb-2">
                                If provided, it will be added before the standard notification and response instructions in the invitation email.
                            </p>
                        </div>
                        <div class="flex-grow-1">
                            <b-button 
                                v-if="!inviteTextEditMode"
                                size="sm"
                                variant="sharp-secondary"
                                class="text-nowrap"
                                @click="inviteTextEditMode = true"
                            >
                                <b-icon icon="pencil" class="mr-1"></b-icon>
                                Edit
                            </b-button>
                            <div v-if="inviteTextEditMode" class="d-flex">
                                <b-button
                                    variant="sharp-secondary"
                                    class="mr-1"
                                    size="sm"
                                    @click="inviteTextEditMode = false"
                                >Cancel</b-button>
                                <b-button
                                    variant="sharp-primary"
                                    size="sm"
                                    @click="updateInviteText"
                                >Save</b-button>
                            </div>
                        </div>
                    </div>
                    
                    <hot-edit-textarea
                        v-model="invite_email_text"
                        ref="inviteEmailTextarea"
                        label=""
                        placeholder="Type your custom invitation message..."
                        :empty-text="(loadingList || loadingInviteText) ? 'Loading...' : 'Not set.  We will use the standard system invite email.'"
                        :edit-mode="inviteTextEditMode"
                        rows="4"
                        max-rows="10"
                        no-edit-class="small bg-gray-100 p-3 rounded"
                        label-vertical
                    ></hot-edit-textarea>
                    <div v-if="inviteTextEditMode" class="">
                        <b-button variant="minimal-primary" size="sm" @click="showInviteTextSnippetModal">
                            <b-icon icon="file-text" class="mr-1"></b-icon>
                            <span class="special-text">Insert Saved Snippet</span>
                        </b-button>
                    </div>
                    <b-modal
                        title="Insert a snippet..."
                        centered
                        size="lg"
                        ref="snippetModal"
                        v-model="inviteTextSnippetModalShowing"
                        body-class="p-0"
                        hide-footer
                    >
                        <snippet-manager
                            areaSlug="rfx_invite_email"
                            mode="insert"
                            @insert="handleInviteTextSnippetInsert"
                        ></snippet-manager>
                    </b-modal>

                </div>
                <h6 
                    v-if="showQuickLinksSettings || showPortalListingsSettings"
                    class="text-muted text-uppercase mt-5"
                >
                    Other Invitation Methods:
                </h6>

                <rfx-manage-quicklinks
                    v-if="showQuickLinksSettings"
                    class="mt-4"
                    :rfx-pk="rfxPk"
                ></rfx-manage-quicklinks>

                <div 
                    v-if="showPortalListingsSettings"
                    class="w-100 border rounded shadow-sm bg-white p-3 mt-4"
                >
                    
                    <div class="d-flex justify-content-between align-items-center">
                        <h4 class="mb-0">Supplier Portal Listing</h4>
                        <instructions-modal
                            modal-title="Portal Listing - Settings"
                            button-size="sm"
                            button-variant="minimal-info"
                        >
                            <ul class="list-flush list-margin-2">
                                <li>Add this RFx to the "open for bids" list in your Supplier Portal.</li>
                                <li>Anyone with access to your Supplier Portal will be able to see this list and submit a response.</li>
                            </ul>
                        </instructions-modal>
                    </div>
                    
                    <hr class="my-2">

                    <b-form>
                        <b-form-checkbox 
                            v-model="portalSettings.portal_listing_enabled"
                            :disabled="loadingPortalSettings"
                            size="md"
                            switch
                            class="mt-3 checkbox-hide-loading"
                            :class="{'loading': loadingPortalSettings}"
                            @change="updatePortalSettings"
                        >
                            <div class="font-weight-600">Add to <b-link :href="splUrlMap.portal_listings" target="_blank" class="">"Open For Bids" page <b-icon icon="box-arrow-up-right" class="ml-2"></b-icon></b-link></div>
                            <ul class="list-flush font-size-80 mt-2">
                                <li>Add this RFx to the "open for bids" list in your Supplier Portal.</li>
                                <li>Anyone with access to your Supplier Portal will be able to see this list and submit a response.</li>
                            </ul>
                        </b-form-checkbox>
                        <b-form-checkbox 
                            v-if="tenantInfo.settings.enable_public_listings"
                            v-model="portalSettings.public_listing_enabled"
                            :disabled="loadingPortalSettings || !portalSettings.portal_listing_enabled"
                            size="md"
                            switch
                            class="mt-2 ml-4 checkbox-hide-loading"
                            :class="{'loading': loadingPortalSettings}"
                            @change="updatePortalSettings"
                        >
                            <div class="font-weight-bold">Also add to your <b-link :href="splUrlMap.public_listings" target="_blank" class="text-reset">PUBLIC "Open For Bids" Page</b-link></div>
                            <ul class="list-flush font-size-90" v-if="portalSettings.public_listing_enabled">
                                <li><span class="text-danger font-weight-bold">Warning: </span>Enabling this will make the entire RFx event publicly visible, including attachments.</li>
                            </ul>
                        </b-form-checkbox>
                    </b-form>
                </div>

            </b-col>

        </b-row>

        <rfx-bulk-reminder-modal
            ref="bulkReminderModal"
            :rfx-id="rfxPk"
        ></rfx-bulk-reminder-modal>

        <state-machine-transitions
            ref="transitionComponent"
            @state-change="handleStateChange"
        ></state-machine-transitions>

        <b-modal
            v-model="bulkInviteModalShowing"
            size="xl"
            title="Bulk Vendor Invitation"
            hide-footer
            centered
            no-close-on-backdrop
        >
            <rfx-vendor-bulk-invite
                :rfx-pk="rfxPk"
                :invite-email-text="invite_email_text"
                :invited-vendor-ids="invitedVendorIdList"
                :can-send-invites="rfxData.can_send_invites"
                @invites-sent="getInvitesList"
                @done="bulkInviteModalShowing = false"
            ></rfx-vendor-bulk-invite>
        </b-modal>

    </div>
</template>

<script>
//import RfxManageQuicklinks from './RfxManageQuicklinks';
import RfxVendorInviteModal from './RfxVendorInviteModal';
import { suppliosApi } from '../js/api-service';
import { FormatDateHelper } from '../js/filters-helpers';
import RfxBulkReminderModal from './RfxBulkReminderModal';
import { HotEditTextarea } from '../js/custom-form-components';
import { splUrlMapMixin, tenantInfoMixin } from '../js/custom-vue-mixins';
import StatusObjectBadge from './functional-comps/StatusObjectBadge';
import cloneDeep from 'lodash/cloneDeep';
import StateMachineTransitions from './StateMachineTransitions';
import { RfxResponse } from '../js/models/rfxResponse';
import { Rfx } from '../js/models/rfx';
import SnippetManager from './snippets/SnippetManager.vue';
// import RfxVendorBulkInvite from './RfxVendorBulkInvite.vue';  // dynamic import instead
const RfxVendorBulkInvite = () => import('./RfxVendorBulkInvite.vue');

export default {
    name: 'RfxManageInvites',
    components: {
        //RfxManageQuicklinks,
        RfxVendorInviteModal,
        RfxBulkReminderModal,
        HotEditTextarea,
        StatusObjectBadge,
        StateMachineTransitions,
        SnippetManager,
        RfxVendorBulkInvite,
    },
    mixins: [
        splUrlMapMixin,
        tenantInfoMixin,
    ],
    props: {
        rfxPk: {
            type: [String, Number],
            required: true,
        },
        rtuRole: {  // role of the RfxTenantUser - 'admin', 'decision', 'requester'
            type: String,
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingList: false,
            inviteTextEditMode: false,  // toggle for editing invite_email_text
            loadingInviteText: false,  // loading flag when updating invite_email_text
            invite_email_text: '',  // will be nested in rfxData, but we'll pull it out and manage separately
            rfxData: {},  // various data related to this RFX
            responseList: [],  // list of current RfxResponses in any status
            responseListFields: [
                {
                    key: 'vendor_name',
                    label: 'Supplier',
                    sortable: true,
                },
                {
                    key: 'sm_state',
                    label: 'Response Stage',
                    sortable: true,
                    sortByFormatted: true,
                    class: "text-center",
                },
                {
                    key: 'actions',
                    label: 'Actions',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                }
            ],
            bulkInviteModalShowing: false,
            vendorInviteModal: false,
            // portal listing settings-- also see computed properties for booleans of showing these or not
            loadingPortalSettings: false,
            portalSettings: {
                portal_listing_enabled: false,
                public_listing_enabled: false,
            },
            // INVITE TEXT SNIPPETS
            inviteTextSnippetPosition: 0,  // index/position in the textarea where we insert... we capture it when user clicks to insert snippet
            inviteTextSnippetModalShowing: false,  // v-model with the modal
        }
    },
    computed: {
        invitedVendorList: function() {
            // an array of vendor objects who are already invited so you can use this in modal
            return this.responseList.map(resp => resp.vendor);
        },
        invitedVendorIdList() {
            // just the vendor IDs, not full objects
            return this.responseList.map(resp => resp.vendor.id);
        },
        showQuickLinksSettings: function() {
            // only show quicklinks if open registrations (allow_open_signups) are ON for Tenant -- until you have a way of doing special registration for quicklinks
            return !!this.tenantInfo && !!this.tenantInfo.settings && !!this.tenantInfo.settings.allow_open_signups;
        },
        showPortalListingsSettings: function() {
            // only show portal listings settings if they are enabled for the Tenant
            return !!this.tenantInfo && !!this.tenantInfo.settings && !!this.tenantInfo.settings.enable_portal_listings;
        },
    },
    filters: {
        dateFilter: function(value) {
            return FormatDateHelper(value);
        },
    },
    mounted: function() {
        this.getInitialData();
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        clearError: function() {
            this.errorMsg = 'Error';
            this.error = false;
        },
        setResponseList(respListFromApi) {
            // DRY helper to set this.responseList from API data - used in multiple methods below (annoying)
            this.responseList = cloneDeep(respListFromApi).map(resp => new RfxResponse(resp));
        },
        getInitialData: function() {
            // gets various data needed on load, including data on Rfx and related Responses (which includes invitations)
            this.loadingList = true;
            suppliosApi.rfx.manageInvitesData(this.rfxPk)
            .then(response => {
                console.log('got initial data - response below');
                console.log(response);
                const rfxData = cloneDeep(response.data.rfx);  // not sure why you cloned it... 
                this.rfxData = new Rfx(rfxData);
                this.setPortalSettings(rfxData);  // shared method to do this, DRY
                this.setResponseList(response.data.responses);  // shared method, sets local data
                this.invite_email_text = this.rfxData.invite_email_text;
            })
            .catch(err => this.handleError(err, 'Error while loading initial data'))
            .finally(() => this.loadingList = false)
        },
        getInvitesList: function() {
            // re-fetch just the list of invites, not other stuff from getInitialData
            this.loadingList = true;
            return suppliosApi.rfx.getInvitesList(this.rfxPk)
                .then(response => {
                    this.setResponseList(response.data);  // shared method, sets local data
                    //this.responseList = cloneDeep(response.data);
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while refreshing list of invites');
                    return false;
                })
                .then(success => {
                    this.loadingList = false;
                    return success;
                });
        },
        setPortalSettings: function(rfxDataObj) {
            // take a full response from rfxData and pull out portal_listing_enabled, public_listing_enabled
            this.portalSettings.portal_listing_enabled = rfxDataObj.portal_listing_enabled;
            this.portalSettings.public_listing_enabled = rfxDataObj.public_listing_enabled;
        },
        updatePortalSettings: function() {
            this.loadingPortalSettings = true;
            this.$nextTick(() => {  // wrap this in nextTick so data can update, since you aren't passing to function directly
                let updateData = {...this.portalSettings};
                return suppliosApi.rfx.update_invite_settings(this.rfxPk, updateData)  // this is a PATCH call, used in other components too
                    .then(response => {
                        console.log('response from udpating portal settings');
                        console.log(response);
                        this.setPortalSettings(response.data);  // make sure you know what response format is... likely the output of whatever serializer you used for update
                        return true;
                    })
                    .catch(error => {
                        this.handleError(error, 'Error while updating Portal Settings');
                        return false;
                    })
                    .then(wasSuccess => {
                        this.loadingPortalSettings = false;
                        return wasSuccess;
                    });
            });
        },
        updateInviteText: function() {
            this.loadingInviteText = true;
            let updateData = {
                invite_email_text: this.invite_email_text,
            };
            return suppliosApi.rfx.partialUpdate(this.rfxPk, updateData)
                .then(response => {
                    this.invite_email_text = response.data.invite_email_text;
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while updating the invite email text');
                    return false;
                })
                .then(success => {
                    this.loadingInviteText = false;
                    this.inviteTextEditMode = false;
                    return success;
                })
        },
        getInviteEmailTextarea() {
            // get the BS-Vue textarea component for editing the email text - used in a couple places, just a helper for that
            // returns undefined if can't find it (maybe not in the right mode)
            // returns the b-form-textarea component (or undefined)
            return this.$refs['inviteEmailTextarea']?.$refs['textarea'];
        },
        showInviteTextSnippetModal() {
            // we will also record the current cursor position in the textarea so we know where to insert
            // we do this before it loses focus
            // have to traverse refs for now... to our HotEditTextarea, then to its ref on the BFormTextarea, then get .selectionEnd property from that
            const textarea = this.getInviteEmailTextarea();
            this.inviteTextSnippetPosition = textarea ? textarea.selectionEnd : 0;
            this.inviteTextSnippetModalShowing = true;
        },
        handleInviteTextSnippetInsert(content) {
            // get the textarea, insert content at our stashed position
            // we use setRangeText(replacement, start) - that is native JS way to insert text (focus it first)
            // but the BS-Vue textarea also implements it, so call it on the BS-Vue textarea component
            console.log('insert this content at position', this.inviteTextSnippetPosition, content);
            const textarea = this.getInviteEmailTextarea();
            if (!textarea) {
                console.error('Cannot find textarea to insert the snippet!');
                return;
            }
            textarea.focus();
            textarea.setRangeText(content);  // the bs-vue implementation doesn't seem to support args of (content, startPosition)
            // just setting the text does not trigger COMPONENT to update... so we need to do it manually...
            const newValue = textarea.$el.value;  // that will have the new text
            this.invite_email_text = newValue;  // that will update vue component, and update the textarea
            // done, now close modal
            this.inviteTextSnippetModalShowing = false;
        },
        startUpdateInvite: function(inviteObj) {
            this.$refs.vendorInviteModal.updateExisting(inviteObj);
        },
        startBulkReminder: function() {
            this.$refs.bulkReminderModal.openModal();
        },
        // STATE MACHINE TRANSITIONS
        triggerTransition(transObj, endpoint, objectId) {
            // call this from some button, etc. in this component, getting trigger from possible_transitions
            // use transition component to call the transition, showing modal to collect_data if needed
            // using NEW generic version of StateMachineTransition component - so we need to pass in full transition object, string name of suppliosApi endpoint, and object ID
            console.log(`From parent component, we will try calling trigger >${transObj.trigger}< using transition component`);
            const transComp = this.$refs['transitionComponent'];
            if (!transComp) return;
            transComp.trigger(transObj, endpoint, objectId);
        },
        handleStateChange(apiResp) {
            // apiResp will be from transitions component -- likely { success: true, message: 'something', object: rfxData (sparse) }
            console.log('STATE CHANGED - RESPONSE BELOW');
            console.log(apiResp);
            this.getInvitesList();  // if this only updates response/invite list, just reload that
        },
    }
}
</script>

<style>

</style>